import { SafeHydrate } from "@/SafeHydrate";
import { Quote, Star, MapPin, Calendar, ArrowRight, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_SOFT = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const REVIEWS = [{
  rating: 5,
  quote: "Three years on the Annual plan now. Adam visits every December, hands me a photo report, never once had a breakdown. A spring loosened in year two — flagged on the report and replaced free under the warranty. Best money we spend each year.",
  name: "Sarah &amp; Mark T.",
  suburb: "Manly",
  plan: "Annual · Year 3",
  pill: "Long-term plan"
}, {
  rating: 5,
  quote: "We're 200 metres from the beach — salt air absolutely murdered our last set of cables in three years flat. Gold put us on Premium Bi-Annual when they saw the rust, do autumn and spring visits with proper anti-corrosion treatment. Going on four years now and the cables are still original.",
  name: "David L.",
  suburb: "Coogee",
  plan: "Premium Bi-Annual · Year 4",
  pill: "Coastal home",
  featured: true
}, {
  rating: 5,
  quote: "Selling the place and the buyer's inspector wanted maintenance records. Gold pulled our file, stamped &amp; dated three years of photo reports. Sold $25k over reserve — the agent said the documentation 'closed the deal'. Wouldn't have even thought to ask without them.",
  name: "Jenna K.",
  suburb: "Lane Cove",
  plan: "Pre-sale audit pack",
  pill: "Pre-sale check"
}];
export default function ServiceTestimonials() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Paper grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      {/* Corner ornaments */}
      <div aria-hidden className="absolute top-10 right-10 pointer-events-none" style={{
      opacity: 0.5,
      transform: "rotate(90deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>
      <div aria-hidden className="absolute bottom-10 left-10 pointer-events-none" style={{
      opacity: 0.5,
      transform: "rotate(270deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD_DARK
          }}>
              08 · From the Service Log
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: NAVY,
          marginBottom: "20px"
        }}>
            Three Sydney homes.
            <br />
            <span style={{
            color: GOLD_DARK
          }}>
              Three years of zero breakdowns.
            </span>
          </h2>

          <div className="flex items-center gap-3">
            <div className="flex gap-1">
              {[0, 1, 2, 3, 4].map(i => <span key={i} aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <Star size={16} color={GOLD_DARK} fill={GOLD_DARK} strokeWidth={0} />
                </span>)}
            </div>
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "14px",
            fontWeight: 700,
            color: NAVY
          }}>
              5.0
            </span>
            <span style={{
            fontFamily: FONT_BODY,
            fontSize: "14px",
            color: INK_SOFT
          }}>
              · 200+ Google reviews · Sydney-wide
            </span>
          </div>
        </div>

        {/* Cards grid */}
        <div className="grid md:grid-cols-3 gap-5">
          {REVIEWS.map(r => <article key={r.name} className="relative rounded-2xl overflow-hidden transition-all hover:translate-y-[-3px]" style={{
          backgroundColor: "#ffffff",
          border: r.featured ? `2px solid ${GOLD}` : `1px solid rgba(8,20,56,0.08)`,
          boxShadow: r.featured ? `0 20px 60px ${GOLD}33, 0 0 0 1px ${GOLD}55` : "0 4px 14px rgba(8,20,56,0.05)"
        }}>
              {/* Featured ribbon */}
              {r.featured && <div className="absolute top-0 left-0 right-0 h-1" style={{
            background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`
          }} />}

              {/* Big quote mark watermark */}
              <div aria-hidden className="absolute pointer-events-none select-none" style={{
            top: "-30px",
            right: "-10px",
            fontFamily: FONT_HEAD,
            fontSize: "180px",
            fontWeight: 900,
            lineHeight: 1,
            color: `${GOLD}12`,
            letterSpacing: "-0.05em"
          }}>
                "
              </div>

              <div className="relative p-7 md:p-8">
                {/* Pill + stars */}
                <div className="flex items-center justify-between mb-5">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full" style={{
                backgroundColor: r.featured ? `${GOLD}20` : `${GOLD}10`,
                border: `1px solid ${GOLD}33`
              }}>
                    <span style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: GOLD_DARK
                }}>
                      {r.pill}
                    </span>
                  </div>
                  <div className="flex gap-0.5">
                    {Array.from({
                  length: r.rating
                }).map((_, i) => <span key={i} aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                        <Star size={13} color={GOLD_DARK} fill={GOLD_DARK} strokeWidth={0} />
                      </span>)}
                  </div>
                </div>

                {/* Quote icon */}
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex mb-3">
                  <Quote size={22} color={GOLD} strokeWidth={2.25} />
                </span>

                {/* Quote text */}
                <p style={{
              fontFamily: FONT_BODY,
              fontSize: "15px",
              lineHeight: 1.7,
              color: INK,
              marginBottom: "28px",
              fontWeight: 500
            }} dangerouslySetInnerHTML={{
              __html: r.quote
            }} />

                {/* Attribution */}
                <div className="pt-5 border-t" style={{
              borderColor: "rgba(8,20,56,0.1)"
            }}>
                  <div style={{
                fontFamily: FONT_HEAD,
                fontWeight: 800,
                fontSize: "15px",
                color: NAVY,
                letterSpacing: "-0.01em",
                marginBottom: "6px"
              }} dangerouslySetInnerHTML={{
                __html: r.name
              }} />

                  <div className="flex items-center gap-1.5 mb-2">
                    <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                      <MapPin size={11} color={INK_SOFT} strokeWidth={2.5} />
                    </span>
                    <span style={{
                  fontFamily: FONT_BODY,
                  fontSize: "12px",
                  color: INK_SOFT,
                  fontWeight: 600
                }}>
                      {r.suburb}, Sydney
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                      <SafeHydrate><Calendar size={11} color={GOLD_DARK} strokeWidth={2.5} /></SafeHydrate>
                    </span>
                    <span style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "11px",
                  fontWeight: 700,
                  color: GOLD_DARK,
                  letterSpacing: "0.04em"
                }}>
                      {r.plan}
                    </span>
                  </div>
                </div>
              </div>
            </article>)}
        </div>

        {/* Bottom strip */}
        <div className="mt-12 rounded-xl p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-5" style={{
        backgroundColor: CREAM_DEEP,
        border: `1px dashed ${GOLD}55`
      }}>
          <div>
            <div style={{
            fontFamily: FONT_HEAD,
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: GOLD_DARK,
            marginBottom: "6px"
          }}>
              Join the plan
            </div>
            <h4 style={{
            fontFamily: FONT_HEAD,
            fontWeight: 800,
            fontSize: "1.15rem",
            color: NAVY,
            letterSpacing: "-0.01em",
            lineHeight: 1.3
          }}>
              Your annual service slot opens with one call — we hold the
              window.
            </h4>
          </div>
          <div className="flex flex-wrap items-center gap-3 flex-shrink-0">
            <a href="tel:0485000933" className="inline-flex items-center gap-2 px-5 py-3 rounded-full transition-all hover:scale-[1.02]" style={{
            backgroundColor: NAVY,
            color: "#ffffff",
            fontFamily: FONT_HEAD,
            fontWeight: 700,
            fontSize: "13px",
            boxShadow: `0 6px 18px ${NAVY}33`
          }}>
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                <Phone size={14} strokeWidth={2.5} />
              </span>
              Book a service
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                <ArrowRight size={13} strokeWidth={2.5} />
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>;
}